import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Database,
  BrainCircuit,
  KanbanSquare,
  ArrowUpRight,
  Sparkles,
} from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const Home = () => {
  const { t } = useT();
  const expertises = [
    { key: "cyber", icon: ShieldCheck, title: t("expertises.cyber.title"), desc: t("expertises.cyber.short"), bg: "hx-bg-sky" },
    { key: "bigdata", icon: Database, title: t("expertises.bigdata.title"), desc: t("expertises.bigdata.short"), bg: "hx-bg-mint" },
    { key: "ai", icon: BrainCircuit, title: t("expertises.ai.title"), desc: t("expertises.ai.short"), bg: "hx-bg-lavender" },
    { key: "pm", icon: KanbanSquare, title: t("expertises.pm.title"), desc: t("expertises.pm.short"), bg: "hx-bg-peach" },
  ];
  const sectors = t("sectors");
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hx-dot-grid opacity-50" />
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#1093f0]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-12 hx-fadeup">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1093f0]/10 border border-[#1093f0]/30 text-[#0b6fb8] text-xs font-medium mb-8">
                <Sparkles className="w-3.5 h-3.5" />
                {t("hero.badge")}
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[1.02] font-semibold text-[#131517]">
                {t("hero.title1")}<br />
                {t("hero.title2")} <span className="italic font-normal text-[#1093f0]">{t("hero.title3")}</span>
                <span className="hx-cursor-blink" />
              </h1>
              <p className="mt-8 text-lg lg:text-xl text-[#3f444b] max-w-2xl leading-relaxed">
                {t("hero.desc")}
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-contact-cta"
                  className="hx-btn-primary inline-flex items-center gap-2 bg-[#1f2226] text-white px-7 py-4 rounded-full text-sm font-medium"
                >
                  {t("common.startProject")}
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/contact?type=join"
                  data-testid="hero-join-cta"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm font-medium border border-[#1f2226]/15 text-[#1f2226] hover:border-[#1093f0] hover:text-[#1093f0] transition-colors"
                >
                  {t("common.joinAdventure")}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* clients marquee */}
        <div className="border-y border-black/5 bg-[#f4f5f6] overflow-hidden">
          <div className="hx-marquee py-6 mono text-sm uppercase tracking-[0.2em] text-[#6b7079]">
            {[...Array(2)].map((_, loop) => (
              <React.Fragment key={loop}>
                {Array.isArray(sectors) && sectors.map((s, idx) => (
                  <React.Fragment key={`${loop}-${idx}`}>
                    <span>{s}</span>
                    <span>·</span>
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* EXPERTISES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-28">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-5">
            <div className="text-xs mono uppercase tracking-[0.3em] text-[#1093f0] mb-4">{t("sections.expertiseKicker")}</div>
            <h2 className="text-4xl lg:text-5xl font-semibold text-[#131517] leading-[1.05]">
              {t("sections.expertiseTitle1")}
              <span className="block mt-[2mm] italic font-normal">{t("sections.expertiseTitle2")}</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-lg text-[#3f444b] leading-relaxed">
              {t("sections.expertiseDesc")}
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {expertises.map(({ key, icon: Icon, title, desc, bg }) => (
            <div
              key={key}
              className={`hx-card group ${bg} border border-transparent rounded-3xl p-8 flex flex-col h-full`}
              data-testid={`expertise-card-${key}`}
            >
              <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center mb-6 group-hover:bg-[#131517] transition-colors">
                <Icon className="w-6 h-6 text-[#131517] group-hover:text-[#1093f0] transition-colors" />
              </div>
              <h3 className="text-2xl font-semibold text-[#131517] mb-3">{title}</h3>
              <p className="text-sm text-[#3f444b] leading-relaxed flex-1">{desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <Link
            to="/expertise"
            className="inline-flex items-center gap-2 text-[#131517] font-medium hx-link-underline"
            data-testid="expertise-see-all"
          >
            {t("common.seeAll")} <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* DELIVERY MODELS preview */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-28">
        <div className="grid lg:grid-cols-12 gap-12 items-start mb-16">
          <div className="lg:col-span-5">
            <div className="text-xs mono uppercase tracking-[0.3em] text-[#1093f0] mb-4">{t("sections.modelsKicker")}</div>
            <h2 className="text-4xl lg:text-5xl font-semibold text-[#131517]">
              {t("sections.modelsTitle")}
            </h2>
          </div>
          <p className="lg:col-span-6 lg:col-start-7 text-lg text-[#3f444b]">
            {t("sections.modelsDesc")}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {[
            { tag: "m1", title: t("models.m1.title"), desc: t("models.m1.desc"), bg: "hx-bg-mint" },
            { tag: "m2", title: t("models.m2.title"), desc: t("models.m2.desc"), bg: "hx-bg-peach" },
            { tag: "m3", title: t("models.m3.title"), desc: t("models.m3.desc"), bg: "hx-bg-lavender" },
          ].map((m) => (
            <div
              key={m.tag}
              className={`hx-card ${m.bg} border border-transparent rounded-3xl p-8`}
              data-testid={`delivery-model-${m.tag}`}
            >
              <h3 className="text-2xl font-semibold mb-4">{m.title}</h3>
              <p className="text-sm text-[#3f444b] leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-10">
          <Link to="/modeles" className="inline-flex items-center gap-2 text-[#131517] font-medium hx-link-underline" data-testid="models-see-all">
            {t("common.seeAll")} <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* WHY HECNOS */}
      <section className="bg-[#f4f5f6] py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <div className="text-xs mono uppercase tracking-[0.3em] text-[#1093f0] mb-4">{t("sections.whyKicker")}</div>
              <h2 className="text-4xl lg:text-5xl font-semibold text-[#131517] leading-[1.05]">
                {t("sections.whyTitle1")} <br />
                <span className="italic font-normal">{t("sections.whyTitle2")}</span>
              </h2>
            </div>
            <p className="lg:col-span-6 lg:col-start-7 text-lg text-[#3f444b]">
              {t("sections.whyDesc")}
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-5">
            {[
              { title: t("pillars.p1.title"), desc: t("pillars.p1.desc") },
              { title: t("pillars.p2.title"), desc: t("pillars.p2.desc") },
              { title: t("pillars.p3.title"), desc: t("pillars.p3.desc") },
            ].map((c, i) => (
              <div key={i} className="bg-white border border-black/5 rounded-3xl p-8 hx-card" data-testid={`why-card-${i}`}>
                <h3 className="text-xl font-semibold mb-3">{c.title}</h3>
                <p className="text-sm text-[#3f444b] leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-28">
        <div className="relative overflow-hidden rounded-3xl bg-[#131517] text-white p-12 lg:p-20 hx-grain">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#1093f0]/20 blur-3xl" />
          <div className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <div className="text-xs mono uppercase tracking-[0.3em] text-[#1093f0] mb-4">{t("common.next")}</div>
              <h2 className="text-4xl lg:text-6xl font-semibold leading-[1.05]">
                {t("sections.ctaTitle")}
              </h2>
              <p className="mt-6 text-lg text-[#9ca0a6] max-w-xl">
                {t("sections.ctaDesc")}
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <Link
                to="/contact"
                className="hx-btn-primary inline-flex items-center justify-between gap-2 bg-[#1093f0] text-white px-7 py-5 rounded-full text-sm font-semibold"
                data-testid="cta-contact-btn"
              >
                {t("nav.contact")} <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact?type=join"
                className="inline-flex items-center justify-between gap-2 border border-white/15 text-white px-7 py-5 rounded-full text-sm font-medium hover:border-[#1093f0]"
                data-testid="cta-join-btn"
              >
                {t("common.joinAdventure")} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
